import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Input, Select, Radio, Checkbox, Spin, message } from 'antd';
import type { TextAreaRef } from 'antd/lib/input/TextArea';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  RetweetOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useContactUsers } from '../hooks/useContactUsers';
import { additionalAPI } from '../services/additional-api';
import { businessCardAPI } from '../services/api';
import { dataUrlToFile } from '../utils/blobUtils';
import KeywordSelectModal from './KeywordSelectModal';
import ImagePreviewCarouselModal, { CapturedItem } from './ImagePreviewCarouselModal';

export type { CapturedItem };

// まとめてアップロード時の仮登録/本登録方式・重複整備の保存キー（クライアント側に前回設定を保持）
const STORAGE_KEY_IMPORT_MODE = 'multiUploadImportMode';
const STORAGE_KEY_MERGE_REMARKS = 'multiUploadDoMergeRemarks';

interface MultiImageUploadFlowProps {
  /** 確認・アップロード対象の画像一覧 */
  images: CapturedItem[];
  /** 画像配列の変更（削除）コールバック */
  onChangeImages: (images: CapturedItem[]) => void;
  /** 戻る（連続撮影なら撮影に戻る／アップロードならメニューへ等） */
  onBack: () => void;
  /** 戻るボタンのラベル（既定: 戻る） */
  backLabel?: string;
  /** アップロード完了後のOK押下時 */
  onComplete: () => void;
}

const fullScreenStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: '100%',
  margin: 0,
  padding: 0,
  overflow: 'hidden',
  backgroundColor: '#000',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const backButtonStyle: React.CSSProperties = {
  color: 'white',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  borderColor: 'white',
  borderWidth: '2px',
  width: '44px',
  height: '44px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

/**
 * 連続撮影の確認画面〜アップロード（情報入力モーダル・キーワード・進捗・完了）を
 * 一括で扱う共通コンポーネント。連続撮影フローと画像アップロードフローで共通利用する。
 */
const MultiImageUploadFlow: React.FC<MultiImageUploadFlowProps> = ({
  images,
  onChangeImages,
  onBack,
  backLabel = '戻る',
  onComplete,
}) => {
  const { user } = useAuth();
  // 担当者一覧（sys ユーザーでログインした場合はログイン中の本人が補完される）
  const { contactUsers } = useContactUsers();

  // 確認画面の選択中インデックス
  const [confirmIndex, setConfirmIndex] = useState<number>(0);
  // 拡大プレビュー
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [previewIndex, setPreviewIndex] = useState<number>(0);

  // アップロード情報入力モーダル
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadComplete, setUploadComplete] = useState<boolean>(false);
  // アップロード中断用（タイムアウト／キャンセルボタン共通）
  const uploadAbortRef = useRef<AbortController | null>(null);
  const uploadCancelledRef = useRef<boolean>(false);

  // 担当者・登録方法・備考・重複整備
  const [selectedContactUser, setSelectedContactUser] = useState<number | undefined>(undefined);
  const contactDefaultSetRef = useRef<boolean>(false);
  const [importMode, setImportMode] = useState<number>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_IMPORT_MODE);
    const parsed = saved !== null ? parseInt(saved, 10) : NaN;
    return parsed === 1 || parsed === 2 ? parsed : 2;
  });
  const handleImportModeChange = (val: number) => {
    setImportMode(val);
    localStorage.setItem(STORAGE_KEY_IMPORT_MODE, String(val));
  };
  const [doMergeRemarks, setDoMergeRemarks] = useState<boolean>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_MERGE_REMARKS);
    return saved === null ? true : saved === 'true';
  });
  const handleDoMergeRemarksChange = (val: boolean) => {
    setDoMergeRemarks(val);
    localStorage.setItem(STORAGE_KEY_MERGE_REMARKS, String(val));
  };
  const [remarksInput, setRemarksInput] = useState<string>('');
  const remarksTextAreaRef = useRef<TextAreaRef>(null);

  // 備考のキーワード入力モーダル
  const [keywordModalVisible, setKeywordModalVisible] = useState<boolean>(false);
  const [keywordOptions, setKeywordOptions] = useState<any[]>([]);
  const [keywordLoadError, setKeywordLoadError] = useState<boolean>(false);
  const keywordsLoadedRef = useRef<boolean>(false);

  // 担当者プルダウン options（DB の担当者一覧）
  const contactSelectOptions = useMemo(
    () =>
      contactUsers.map((u) => ({
        value: u.userno,
        label: u.deptname ? `${u.username} (${u.deptname})` : u.username,
      })),
    [contactUsers]
  );

  // ログインユーザーを初期担当者に設定
  useEffect(() => {
    if (contactDefaultSetRef.current) return;
    if (!user) return;
    if (contactUsers.length === 0) return;
    const selfNo = user.userno ?? user.id;
    const self = contactUsers.find((u) => u.userno === selfNo);
    if (!self) return;
    setSelectedContactUser(self.userno);
    contactDefaultSetRef.current = true;
  }, [user, contactUsers]);

  // 画像が0枚になったら戻る、選択indexを補正
  useEffect(() => {
    if (images.length === 0) {
      setConfirmIndex(0);
      onBack();
    } else {
      setConfirmIndex((cur) => Math.min(cur, images.length - 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length]);

  // キーワード一覧取得（初回のみ）
  const loadKeywords = async () => {
    try {
      const keywordResult = await additionalAPI.getKeywordList('01');
      if (keywordResult.success && keywordResult.data) {
        let keywordData: any = keywordResult.data;
        if (typeof keywordData === 'string') keywordData = JSON.parse(keywordData);
        if (keywordData?.keywordlist && Array.isArray(keywordData.keywordlist)) {
          setKeywordOptions(keywordData.keywordlist);
          setKeywordLoadError(false);
        } else if (keywordData?.result && Array.isArray(keywordData.result)) {
          setKeywordOptions(
            keywordData.result.map((item: any) => ({
              keyword: item.keywordName || '',
              keywordcontent: item.keywordContent || item.keywordName || '',
            }))
          );
          setKeywordLoadError(false);
        } else {
          setKeywordOptions([]);
          setKeywordLoadError(true);
        }
      } else {
        setKeywordOptions([]);
        setKeywordLoadError(true);
      }
    } catch (e) {
      console.error('キーワード取得エラー:', e);
      setKeywordOptions([]);
      setKeywordLoadError(true);
    }
  };

  const handleOpenKeywordModal = async () => {
    setKeywordModalVisible(true);
    if (!keywordsLoadedRef.current) {
      keywordsLoadedRef.current = true;
      await loadKeywords();
    }
  };

  // 備考のカーソル位置にキーワードを挿入
  const insertKeyword = (textToInsert: string) => {
    const textarea = remarksTextAreaRef.current?.resizableTextArea?.textArea;
    const current = remarksInput || '';
    const start = textarea?.selectionStart ?? current.length;
    const end = textarea?.selectionEnd ?? current.length;
    const newValue = current.substring(0, start) + textToInsert + current.substring(end);
    setRemarksInput(newValue);
    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        const pos = start + textToInsert.length;
        textarea.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleDeleteConfirmImage = (index: number) => {
    onChangeImages(images.filter((_, i) => i !== index));
  };

  const handleConfirm = () => {
    if (images.length === 0) return;
    setConfirmModalOpen(true);
  };

  // アップロード実行
  const handleUpload = async () => {
    if (images.length === 0) return;
    const uploadFiles = images.map((item, idx) =>
      dataUrlToFile(item.extracted || item.original, `card_${idx + 1}.jpg`)
    );
    const param = {
      importType: 9, // UP-TONE（連続撮影・画像アップロードの種別をまとめて 9）
      importMode,
      doMergeRemarks,
      keyword: remarksInput,
      isAddDefaultRemarks: false,
      // 担当者は contactusernoList に userno の配列(Number[])で渡す。
      contactusernoList: selectedContactUser != null ? [selectedContactUser] : [],
      remarks: '',
    };

    // 30 秒でタイムアウト。キャンセルボタンからも中断できるよう AbortController を保持する。
    const controller = new AbortController();
    uploadAbortRef.current = controller;
    uploadCancelledRef.current = false;

    setConfirmModalOpen(false);
    setUploading(true);
    try {
      const res = await businessCardAPI.uploadImage(uploadFiles, param, {
        signal: controller.signal,
        timeoutMs: 30000,
      });
      if (res?.aborted) {
        // タイムアウト or ユーザーによるキャンセル。確認画面に戻して再試行できるようにする。
        setUploading(false);
        if (uploadCancelledRef.current) {
          message.info('アップロードをキャンセルしました');
        } else {
          message.error('通信がタイムアウトしました。通信環境をご確認のうえ、もう一度お試しください。');
        }
        setConfirmModalOpen(true);
        return;
      }
      if (res?.statuscode === 200) {
        setUploading(false);
        setUploadComplete(true);
      } else {
        setUploading(false);
        message.error(res?.message || 'アップロードに失敗しました');
        setConfirmModalOpen(true);
      }
    } catch (error: any) {
      setUploading(false);
      message.error(error?.message || 'アップロードに失敗しました');
      setConfirmModalOpen(true);
    } finally {
      uploadAbortRef.current = null;
    }
  };

  // アップロードのキャンセル（進行中の通信を中断して確認画面へ戻す）
  const handleCancelUpload = () => {
    // 通信が既に完了している場合（uploadAbortRef が null）は何もしない
    if (!uploadAbortRef.current) return;
    uploadCancelledRef.current = true;
    uploadAbortRef.current?.abort();
  };

  const handleCompleteOk = () => {
    setUploadComplete(false);
    onComplete();
  };

  return (
    <>
      {/* 確認画面（選択中の大プレビュー＋サムネイルスライダー） */}
      <div style={fullScreenStyle}>
        {/* 戻る */}
        <div style={{ position: 'absolute', top: '16px', left: '16px', zIndex: 100 }}>
          <Button
            type="primary"
            shape="circle"
            icon={<ArrowLeftOutlined style={{ fontSize: '20px' }} />}
            onClick={onBack}
            style={backButtonStyle}
          />
        </div>

        {/* 選択中画像の削除 */}
        {images.length > 0 && (
          <div style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 100 }}>
            <Button
              type="primary"
              danger
              shape="circle"
              icon={<DeleteOutlined style={{ fontSize: '18px' }} />}
              onClick={() => handleDeleteConfirmImage(confirmIndex)}
              style={{ width: '44px', height: '44px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            />
          </div>
        )}

        {/* タイトル */}
        <div
          style={{
            position: 'absolute',
            top: '22px',
            left: 0,
            right: 0,
            textAlign: 'center',
            color: 'white',
            fontSize: 16,
            fontWeight: 'bold',
            textShadow: '0 0 4px rgba(0,0,0,0.8)',
            zIndex: 50,
            pointerEvents: 'none',
          }}
        >
          内容の確認（{images.length}枚）
        </div>

        {/* 選択中の画像（タップで拡大） */}
        {images[confirmIndex] && (
          <img
            src={images[confirmIndex].extracted || images[confirmIndex].original}
            alt={`${confirmIndex + 1}枚目`}
            onClick={() => {
              setPreviewIndex(confirmIndex);
              setPreviewOpen(true);
            }}
            style={{ maxWidth: '100%', maxHeight: '52vh', objectFit: 'contain', cursor: 'zoom-in' }}
          />
        )}

        {/* サムネイルスライダー */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 'calc(100px + env(safe-area-inset-bottom, 0px) + var(--flutter-footer-height, 0px))',
            zIndex: 50,
            display: 'flex',
            gap: '8px',
            padding: '8px 12px',
            overflowX: 'auto',
          }}
        >
          {images.map((item, idx) => {
            const src = item.extracted || item.original;
            return (
              <div key={idx} style={{ position: 'relative', flexShrink: 0 }}>
                <img
                  src={src}
                  alt={`${idx + 1}枚目`}
                  onClick={() => setConfirmIndex(idx)}
                  style={{
                    width: 60,
                    height: 60,
                    objectFit: 'cover',
                    borderRadius: 6,
                    cursor: 'pointer',
                    display: 'block',
                    border: idx === confirmIndex ? '3px solid #0693e3' : '3px solid rgba(255,255,255,0.6)',
                    boxShadow: idx === confirmIndex ? '0 0 6px rgba(6,147,227,0.9)' : 'none',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    bottom: 2,
                    left: 2,
                    background: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    fontSize: 10,
                    padding: '0 4px',
                    borderRadius: 3,
                    lineHeight: '14px',
                  }}
                >
                  {idx + 1}
                </div>
              </div>
            );
          })}
        </div>

        {/* 下部ボタン: 戻る / 確定 */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 'calc(28px + env(safe-area-inset-bottom, 0px) + var(--flutter-footer-height, 0px))',
            zIndex: 50,
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
          }}
        >
          <Button shape="round" size="large" icon={<RetweetOutlined />} onClick={onBack}>
            {backLabel}
          </Button>
          <Button type="primary" shape="round" size="large" icon={<CheckOutlined />} onClick={handleConfirm}>
            確定 ({images.length})
          </Button>
        </div>
      </div>

      {/* 拡大プレビュー */}
      <ImagePreviewCarouselModal
        open={previewOpen}
        images={images}
        initialIndex={previewIndex}
        onClose={() => setPreviewOpen(false)}
        onImagesChange={onChangeImages}
      />

      {/* アップロード情報入力モーダル */}
      <Modal
        open={confirmModalOpen}
        title="アップロード情報の入力"
        onCancel={() => setConfirmModalOpen(false)}
        centered
        zIndex={1030}
        footer={[
          <Button key="cancel" onClick={() => setConfirmModalOpen(false)} disabled={uploading}>
            キャンセル
          </Button>,
          <Button key="upload" type="primary" onClick={handleUpload} loading={uploading}>
            アップロード
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 4, fontWeight: 'bold' }}>登録方法</div>
          <Radio.Group
            value={importMode}
            onChange={(e) => handleImportModeChange(e.target.value as number)}
            disabled={uploading}
          >
            <Radio value={1} style={{ display: 'flex', marginBottom: 4 }}>
              全てを仮登録し、確認しながら本登録
            </Radio>
            <Radio value={2} style={{ display: 'flex' }}>
              重複のみを仮登録し、その他は自動で本登録
            </Radio>
          </Radio.Group>
        </div>
        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 4, fontWeight: 'bold' }}>担当者</div>
          <Select
            style={{ width: '100%' }}
            placeholder="担当者を選択"
            value={selectedContactUser}
            onChange={(val) => setSelectedContactUser(val as number)}
            options={contactSelectOptions}
          />
        </div>
        <div>
          <div
            style={{
              marginBottom: 4,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ fontWeight: 'bold' }}>備考</span>
            <Button size="small" type="primary" onClick={handleOpenKeywordModal} disabled={uploading}>
              キーワード入力
            </Button>
          </div>
          <Input.TextArea
            ref={remarksTextAreaRef}
            value={remarksInput}
            onChange={(e) => setRemarksInput(e.target.value)}
            rows={3}
            placeholder="備考を入力"
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <Checkbox
            checked={doMergeRemarks}
            onChange={(e) => handleDoMergeRemarksChange(e.target.checked)}
            disabled={uploading}
          >
            「顧客備考」欄の重複整備を行う
          </Checkbox>
          <div style={{ marginTop: 4, fontSize: 12, color: '#888' }}>
            重複した複数のキーワード（【】付きのキーワード）を一つに統合できます。
          </div>
        </div>
      </Modal>

      {/* 備考のキーワード入力モーダル */}
      <KeywordSelectModal
        open={keywordModalVisible}
        options={keywordOptions}
        onClose={() => setKeywordModalVisible(false)}
        onSelect={insertKeyword}
        error={keywordLoadError}
        destroyOnClose
        maskClosable={false}
        zIndex={1040}
      />

      {/* アップロード中のプログレスモーダル */}
      <Modal
        open={uploading}
        title={null}
        footer={null}
        closable={false}
        maskClosable={false}
        keyboard={false}
        centered
        zIndex={1040}
      >
        <div style={{ textAlign: 'center', padding: '16px 8px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 20, fontSize: 16, fontWeight: 'bold' }}>アップロード中...</div>
          <div style={{ marginTop: 8, color: '#666' }}>
            アップロードが完了するまで画面を閉じないでください
          </div>
          <div style={{ marginTop: 20 }}>
            <Button onClick={handleCancelUpload}>キャンセル</Button>
          </div>
        </div>
      </Modal>

      {/* アップロード完了モーダル */}
      <Modal
        open={uploadComplete}
        title="アップロード完了"
        closable={false}
        maskClosable={false}
        keyboard={false}
        centered
        zIndex={1040}
        footer={[
          <Button key="ok" type="primary" onClick={handleCompleteOk}>
            OK
          </Button>,
        ]}
      >
        <div>AI解析が終了したらメニュー画面の仮登録一覧から確認できるようになります。</div>
      </Modal>
    </>
  );
};

export default MultiImageUploadFlow;
